import * as Sentry from '@sentry/react'
import type { ChallengeService } from '@duck/api-definitions'

const CHART_ID_IN_HTML = /data-chart-id="([^"]+)"/g

const extractChartIdsFromHtml = (html: string): string[] => {
  const ids: string[] = []
  let match
  while ((match = CHART_ID_IN_HTML.exec(html)) !== null) {
    ids.push(match[1])
  }
  return ids
}

/**
 * Verify that every `data-chart-id` in the solution HTML has an entry in the
 * charts map, and vice versa. Reports to Sentry on mismatch but never throws —
 * we'd rather save a partially-wrong state than lose the user's work.
 *
 * Called pre-save to catch the bug described in the May 2026 incident where
 * a stale debounced solution save could revert chart adds/removes.
 */
export const verifyChartsSolutionConsistency = (
  challengeId: string,
  solution: string,
  charts: ChallengeService.WorkState['charts'] | undefined,
  callsite: string,
): void => {
  const idsInHtml = new Set(extractChartIdsFromHtml(solution || ''))
  const idsInMap = new Set(Object.keys(charts || {}))
  const missingInMap: string[] = []
  const missingInHtml: string[] = []
  idsInHtml.forEach((id) => { if (!idsInMap.has(id)) missingInMap.push(id) })
  idsInMap.forEach((id) => { if (!idsInHtml.has(id)) missingInHtml.push(id) })

  if (missingInMap.length === 0 && missingInHtml.length === 0) return

  Sentry.captureMessage('charts/solution mismatch pre-save', {
    level: 'error',
    extra: {
      challengeId,
      callsite,
      missingInMap,
      missingInHtml,
      idsInHtml: [...idsInHtml],
      idsInMap: [...idsInMap],
    },
  })
}
